import { redirect } from 'react-router-dom';
import { peekUserProfile } from '../services/auth';
import { store } from '../store/store';
import type { User } from '../types/User';
import { canUseKnowledgeMap } from '../utils/knowledgeMapFlag';
import { OS_DESKTOP_MEDIA_QUERY } from './osRouteMeta';

/**
 * Where a session opens (ORA-751).
 *
 * The desktop lives at /, but a student with "Open Dashboard by Default" on
 * lands on the Dashboard. Deciding that in an effect meant the desktop had
 * painted before the redirect ran, then the Dashboard dissolved in over it
 * — a flash on every cold load and login. So the / route's loader decides
 * first, before anything renders: from the store when it holds the profile,
 * else from the copy redux-persist wrote last session (a cold load runs the
 * loader before rehydration), else — right after a login, when the store
 * has the session but the profile is still to be fetched — from the server.
 * The shell's effect stays as the fallback for whatever is left (a profile
 * from before the setting on a load that could not ask the server).
 *
 * Decided once per app load, on the first route: later visits to / (the AI
 * nav item, closing a window) are the student's own and stay.
 */

export interface PersistedLandingProfile {
  defaultToDashboard?: boolean;
  isOnboarded?: boolean;
  /** Whether the map is offered, so `?map` is a reason to stay on /. */
  canUseKnowledgeMap?: boolean;
}

const toLandingProfile = (
  profile: Pick<
    User,
    'defaultToDashboard' | 'isOnboarded' | 'role' | 'email' | 'instance'
  >
): PersistedLandingProfile => ({
  defaultToDashboard: profile.defaultToDashboard,
  isOnboarded: profile.isOnboarded,
  canUseKnowledgeMap: canUseKnowledgeMap(profile as User),
});

const PERSIST_ROOT_KEY = 'persist:root';

/** The persisted profile's landing fields; null when there is none to read. */
export const readPersistedLandingProfile =
  (): PersistedLandingProfile | null => {
    try {
      const root = window.localStorage.getItem(PERSIST_ROOT_KEY);
      if (!root) return null;

      // redux-persist stores the root as JSON whose slice values are JSON
      // strings of their own.
      const user: unknown = JSON.parse(
        (JSON.parse(root) as { user?: string }).user ?? 'null'
      );

      const profile = (user as { profile?: User } | null)?.profile;

      if (!profile || typeof profile !== 'object') return null;

      return toLandingProfile(profile);
    } catch {
      return null;
    }
  };

/** The store's profile when it has one, else last session's persisted copy. */
export const readLandingProfile = (): PersistedLandingProfile | null => {
  const live = store.getState().user.profile;
  if (live) return toLandingProfile(live);

  return readPersistedLandingProfile();
};

let isLandingPending = true;

export const landingDecision = {
  isPending: () => isLandingPending,
  settle: () => {
    isLandingPending = false;
  },
  /** Tests only: a fresh app load. */
  reset: () => {
    isLandingPending = true;
  },
};

/** The query parameter that opens the desktop straight into the map. */
export const MAP_QUERY_PARAM = 'map';

/** A thread address or a chat ask in the query is a reason to be on /. */
export const hasChatAskInSearch = (search: string): boolean => {
  const params = new URLSearchParams(search);
  return (
    params.has('conversationId') ||
    params.has('blockId') ||
    params.has('openChat')
  );
};

/**
 * A reason to stay on the desktop rather than take the "Open Dashboard by
 * Default" hop: a chat ask, or `?map` from someone the knowledge map is
 * offered to. The map is desktop-only, so below the OS breakpoint `?map` is
 * never one (phones forward to the Dashboard, dropping it).
 */
export const hasDesktopAskInSearch = (
  search: string,
  canUseMap: boolean
): boolean =>
  hasChatAskInSearch(search) ||
  (canUseMap && new URLSearchParams(search).has(MAP_QUERY_PARAM));

/** The query for a hop to the Dashboard, which has no use for `?map`. */
export const dashboardSearchFor = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(MAP_QUERY_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : '';
};

export const desktopLandingLoader = async ({
  request,
}: {
  request: Request;
}) => {
  const { search } = new URL(request.url);

  // Below the OS breakpoint there is no desktop: / is the Dashboard. The
  // shell forwards there and, for a thread address or a chat ask, opens the
  // sheet on the way; the bare case goes now, so the Dashboard is the first
  // paint rather than something it fades in over.
  if (!window.matchMedia(OS_DESKTOP_MEDIA_QUERY).matches) {
    if (hasChatAskInSearch(search)) return null;
    return redirect(`/dashboard${dashboardSearchFor(search)}`);
  }

  if (!landingDecision.isPending()) return null;

  let profile = readLandingProfile();

  if (
    profile?.defaultToDashboard === undefined &&
    store.getState().auth.isAuthenticated
  ) {
    const fresh = await peekUserProfile({ reuseRecent: true });
    if (fresh) profile = toLandingProfile(fresh);
  }

  // Still nothing to decide from (no session, or a student still in
  // onboarding): the shell's effect decides once the fresh profile is in.
  if (profile?.defaultToDashboard === undefined || !profile.isOnboarded) {
    return null;
  }

  landingDecision.settle();

  if (
    !profile.defaultToDashboard ||
    hasDesktopAskInSearch(search, profile.canUseKnowledgeMap ?? false)
  ) {
    return null;
  }

  return redirect(`/dashboard${dashboardSearchFor(search)}`);
};
